import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faSync, faInfoCircle, faExternalLinkAlt, faCalendarAlt, faUsers, faBriefcase, faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import Dexie from 'dexie';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';
import { Link } from 'react-router-dom';

const API_URL = `${API_CONFIG2.apiUrl}offres/api_offrerecrutement.php`;

// Cache Configuration (Dexie)
const db = new Dexie('OffresPublicCache');
db.version(1).stores({ cache: 'key, timestamp' });
const CACHE_TTL = 24 * 60 * 60 * 1000;

// Helper components pour le design
const SkeletonCard = () => (
    <div className="glass-card skeleton">
        <div className="skeleton-line badge"></div>
        <div className="skeleton-line title"></div>
        <div className="skeleton-line text"></div>
        <div className="skeleton-line text short"></div>
        <div className="skeleton-footer"></div>
    </div>
);

const OffresRecrutementCards = () => {
    const [offres, setOffres] = useState([]);
    const [loading, setLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [pagination, setPagination] = useState({ currentPage: 1, totalPages: 1, totalRecords: 0, limit: 12 });
    const [source, setSource] = useState('cache');
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedOffre, setSelectedOffre] = useState(null);

    const searchTimeoutRef = useRef(null);
    const abortControllerRef = useRef(null);

    // Fonctions de cache (identiques à votre logique Dexie)
    const getCachedData = async (key) => {
        const entry = await db.cache.get(key);
        return (entry && Date.now() - entry.timestamp < CACHE_TTL) ? entry.data : null;
    };

    const loadData = useCallback(async (page = 1, search = '', force = false) => {
        if (abortControllerRef.current) abortControllerRef.current.abort();
        abortControllerRef.current = new AbortController();

        const cacheKey = `offres_v2_${page}_${search}`;
        if (force) setRefreshing(true); else setLoading(true);

        try {
            if (!force) {
                const cached = await getCachedData(cacheKey);
                if (cached) {
                    setOffres(cached.data);
                    setPagination(cached.pagination);
                    setSource('cache');
                    setLoading(false);
                    return;
                }
            }

            const res = await fetch(`${API_URL}?action=fetch&page=${page}&limit=${pagination.limit}&search=${encodeURIComponent(search)}`, { signal: abortControllerRef.current.signal });
            const json = await res.json();

            if (json.status === 'success') {
                setOffres(json.data);
                setPagination(json.pagination);
                setSource(force ? 'live (sync)' : 'live');
                await db.cache.put({ key: cacheKey, data: { data: json.data, pagination: json.pagination }, timestamp: Date.now() });
            }
        } catch (e) {
            console.error(e);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, [pagination.limit]);

    useEffect(() => { loadData(1, '', false); }, []);

    const handleSearch = (e) => {
        const val = e.target.value;
        setSearchTerm(val);
        clearTimeout(searchTimeoutRef.current);
        searchTimeoutRef.current = setTimeout(() => loadData(1, val, false), 400);
    };

    return (
        <div className="pro-recrutement-container">
            <style>{`
                :root {
                    --accent: #64ffda;
                    --accent-glow: rgba(100, 255, 218, 0.3);
                    --bg-deep: #0a192f;
                    --bg-card: #112240;
                    --text-main: #ccd6f6;
                    --text-dim: #8892b0;
                    --glass-border: rgba(255, 255, 255, 0.1);
                }

                .pro-recrutement-container {
                    background: var(--bg-deep);
                    color: var(--text-main);
                    min-height: 100vh;
                    padding: 60px 8%;
                    font-family: 'Inter', system-ui, sans-serif;
                }

                /* Header & Search Section */
                .header-section {
                    text-align: center;
                    margin-bottom: 50px;
                }
                .main-title {
                    font-size: 3rem;
                    font-weight: 800;
                    background: linear-gradient(to right, #fff, var(--accent));
                    -webkit-background-clip: text;
                    -webkit-text-fill-color: transparent;
                    margin-bottom: 10px;
                }
                .search-bar-container {
                    max-width: 700px;
                    margin: 30px auto;
                    position: relative;
                }
                .search-input-wrapper {
                    display: flex;
                    align-items: center;
                    background: rgba(255,255,255,0.05);
                    border: 1px solid var(--glass-border);
                    border-radius: 50px;
                    padding: 5px 25px;
                    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                }
                .search-input-wrapper:focus-within {
                    border-color: var(--accent);
                    box-shadow: 0 0 20px var(--accent-glow);
                    background: rgba(255,255,255,0.08);
                }
                .search-input-wrapper input {
                    background: transparent;
                    border: none;
                    color: #fff;
                    padding: 12px;
                    width: 100%;
                    outline: none;
                    font-size: 1.1rem;
                }

                /* Grid & Cards */
                .cards-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(340px, 1fr));
                    gap: 30px;
                }

                .glass-card {
                    background: var(--bg-card);
                    border: 1px solid var(--glass-border);
                    border-radius: 24px;
                    padding: 30px;
                    transition: all 0.4s ease;
                    display: flex;
                    flex-direction: column;
                    position: relative;
                    cursor: pointer;
                }
                .glass-card:hover {
                    transform: translateY(-10px) scale(1.02);
                    border-color: var(--accent);
                    box-shadow: 0 20px 40px rgba(0,0,0,0.4);
                }
                .glass-card::before {
                    content: '';
                    position: absolute;
                    top: 0; left: 0; right: 0; height: 100%;
                    background: radial-gradient(circle at top right, var(--accent-glow), transparent 70%);
                    opacity: 0;
                    transition: 0.4s;
                    pointer-events: none;
                }
                .glass-card:hover::before { opacity: 1; }

                .badge-service {
                    background: rgba(100, 255, 218, 0.1);
                    color: var(--accent);
                    padding: 6px 14px;
                    border-radius: 8px;
                    font-size: 0.75rem;
                    font-weight: 700;
                    text-transform: uppercase;
                    width: fit-content;
                    margin-bottom: 20px;
                }

                .card-title {
                    font-size: 1.4rem;
                    font-weight: 700;
                    color: #e6f1ff;
                    margin-bottom: 15px;
                }

                .card-desc {
                    color: var(--text-dim);
                    font-size: 0.95rem;
                    line-height: 1.6;
                    margin-bottom: 25px;
                    display: -webkit-box;
                    -webkit-line-clamp: 3;
                    -webkit-box-orient: vertical;
                    overflow: hidden;
                }

                .meta-info {
                    display: flex;
                    justify-content: space-between;
                    border-top: 1px solid rgba(255,255,255,0.05);
                    padding-top: 20px;
                    font-size: 0.85rem;
                    color: var(--text-dim);
                }

                /* Skeletons */
                .skeleton { pointer-events: none; }
                .skeleton-line {
                    background: linear-gradient(90deg, #1d2d50 25%, #233554 50%, #1d2d50 75%);
                    background-size: 200% 100%;
                    animation: loading-pulse 1.5s infinite;
                    border-radius: 4px;
                    margin-bottom: 10px;
                }
                @keyframes loading-pulse {
                    0% { background-position: 200% 0; }
                    100% { background-position: -200% 0; }
                }
                .skeleton-line.badge { width: 80px; height: 20px; border-radius: 8px; }
                .skeleton-line.title { width: 80%; height: 25px; margin-top: 10px; }
                .skeleton-line.text { width: 100%; height: 15px; }

                /* Pagination */
                .pagination-pro {
                    margin-top: 50px;
                    display: flex;
                    justify-content: center;
                    gap: 15px;
                }
                .btn-nav {
                    background: var(--bg-card);
                    border: 1px solid var(--glass-border);
                    color: var(--accent);
                    padding: 12px 25px;
                    border-radius: 12px;
                    transition: 0.3s;
                }
                .btn-nav:hover:not(:disabled) {
                    background: var(--accent);
                    color: var(--bg-deep);
                }

                /* Modal Styling */
                .modal-glass .modal-content {
                    background: #112240;
                    border: 1px solid var(--accent);
                    box-shadow: 0 0 50px rgba(0,0,0,0.5);
                    border-radius: 28px;
                    color: var(--text-main);
                }
                .btn-apply-now {
                    background: var(--accent);
                    color: var(--bg-deep);
                    border: none;
                    padding: 12px 30px;
                    border-radius: 14px;
                    font-weight: 700;
                    transition: 0.3s;
                }
                .btn-apply-now:hover {
                    transform: scale(1.05);
                    box-shadow: 0 0 20px var(--accent-glow);
                }

                @media (max-width: 768px) {
                    .main-title { font-size: 2rem; }
                    .pro-recrutement-container { padding: 40px 20px; }
                }
            `}</style>

            <header className="header-section mt-5" style={{marginTop:'20px'}}>
                <h1 className="main-title">Rejoignez l'Aventure</h1>
                <p style={{ color: 'var(--text-dim)' }}>Explorez nos {pagination.totalRecords} opportunités de carrière</p>
                
                <div className="search-bar-container">
                    <div className="search-input-wrapper">
                        <FontAwesomeIcon icon={faSearch} style={{ color: 'var(--accent)' }} />
                        <input 
                            type="text" 
                            placeholder="Développeur, Manager, Kinshasa..." 
                            value={searchTerm}
                            onChange={handleSearch}
                        />
                        {refreshing && <FontAwesomeIcon icon={faSync} spin style={{ color: 'var(--accent)' }} />}
                    </div>
                </div>
            </header>

            <div className="cards-grid">
                {loading ? (
                    Array(6).fill(0).map((_, i) => <SkeletonCard key={i} />)
                ) : (
                    offres.map((offre) => (
                        <div className="glass-card" key={offre.id} onClick={() => { setSelectedOffre(offre); setShowDetailModal(true); }}>
                            <div className="badge-service">{offre.service}</div>
                            <h3 className="card-title">{offre.titre}</h3>
                            <p className="card-desc">{offre.description}</p>
                            <div className="meta-info">
                                <span><FontAwesomeIcon icon={faUsers} /> {offre.nbPostes} Poste(s)</span>
                                <span><FontAwesomeIcon icon={faCalendarAlt} /> {new Date(offre.datePublication).toLocaleDateString()}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>

            {!loading && offres.length === 0 && (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <FontAwesomeIcon icon={faBriefcase} size="3x" style={{ color: 'var(--glass-border)', marginBottom: '20px' }} />
                    <h3>Aucune offre trouvée</h3>
                    <p style={{ color: 'var(--text-dim)' }}>Essayez de modifier vos termes de recherche.</p>
                </div>
            )}

            <div className="pagination-pro">
                <button className="btn-nav" disabled={pagination.currentPage === 1} onClick={() => loadData(pagination.currentPage - 1, searchTerm)}>Précédent</button>
                <button className="btn-nav" disabled={pagination.currentPage === pagination.totalPages} onClick={() => loadData(pagination.currentPage + 1, searchTerm)}>Suivant</button>
            </div>

            {/* Modal de détail amélioré */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} centered size="lg" className="modal-glass">
                {selectedOffre && (
                    <>
                        <Modal.Header closeButton style={{ border: 'none' }}>
                            <Modal.Title style={{ fontWeight: 800, fontSize: '1.8rem' }}>{selectedOffre.titre}</Modal.Title>
                        </Modal.Header>
                        <Modal.Body style={{ padding: '0 30px 30px' }}>
                            <div style={{ display: 'flex', gap: '15px', marginBottom: '25px' }}>
                                <span className="badge-service">{selectedOffre.service}</span>
                                <span className="badge-service" style={{ background: 'rgba(255,255,255,0.05)', color: '#fff' }}>
                                    <FontAwesomeIcon icon={faMapMarkerAlt} /> Kinshasa, RDC
                                </span>
                            </div>
                            <h5 style={{ color:'var(--accent)' }}>Description du poste</h5>
                            <p style={{ lineHeight: 1.8, color: 'var(--text-dim)' }}>{selectedOffre.description}</p>
                            
                            <div style={{ background: 'rgba(255,255,255,0.03)', padding: '20px', borderRadius: '15px', marginTop: '20px' }}>
                                <p><strong>Nombre de places :</strong> {selectedOffre.nbPostes}</p>
                                <p><strong>Date limite :</strong> {new Date(selectedOffre.datePublication).toLocaleDateString()}</p>
                            </div>
                        </Modal.Body>
                        <Modal.Footer style={{ border: 'none', padding: '0 30px 30px' }}>
                            <Button variant="link" style={{ color: '#fff' }} onClick={() => setShowDetailModal(false)}>Fermer</Button>
                            <Link to={`/postuler/${selectedOffre.titre}/${selectedOffre.id}`} className="btn-apply-now">
                                Postuler à cette offre <FontAwesomeIcon icon={faExternalLinkAlt} />
                            </Link>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default OffresRecrutementCards;